import { useEffect, useRef } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import Step from 'components/global/Step'
import Section from 'components/shared/Section'
import Text from 'components/shared/Text'
import Box from 'components/shared/Box'
import Link from 'components/shared/Link'
import SummaryInfo from 'components/shared/SummaryInfo'
import CardSectionDetails from './CardSectionDetails'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useAutoScroll } from 'utils/hooks'
import { useUnlimited } from 'store/unlimited-slice/hooks'
import { setDeliveryMethod } from 'store/unlimited-slice'
import { useCart } from 'store/cart-slice/hooks'
import { setScrollId } from 'store/general-slice'
import {
    calculateUnlimitedAddToCartEvent,
    pushDataLayer,
} from 'helpers/analytics'
import { Card } from 'store/api-slice/types'

const sortCards = (array: Card[], sortArray: string[]) =>
    [...array].sort(
        (arrA, arrB) =>
            sortArray.indexOf(arrA.cartItemId || '') -
            sortArray.indexOf(arrB.cartItemId || '')
    )

const CardSummary = () => {
    const dispatch = useAppDispatch()
    const { formatMessage } = useIntl()
    const { isCartFetching } = useCart()
    const { calculateScroll } = useAutoScroll()
    const { removeShipping } = useUnlimited()
    const analyticsRef = useRef(false)

    const { cards } = useAppSelector((state) => state.cart.items)
    const { cardBoxes } = useAppSelector((state) => state.unlimited)

    const cardBoxesIds = cardBoxes.map((card) => card.cartItemId || '')
    const sortedCards = sortCards(cards, cardBoxesIds)

    const handleBackToCardEditing = (index: number) => {
        calculateScroll({
            trigerringSection: 'card_configuration',
            trigerringAction: 'edit',
            isRemote: true,
            remoteId: `cardIndexNumber${index}`,
        })
    }

    useEffect(() => {
        // < analytics
        if (isCartFetching || !cards.length) return

        if (!analyticsRef.current) {
            const unlimitedAddToCartEvent =
                calculateUnlimitedAddToCartEvent(cards)
            pushDataLayer(unlimitedAddToCartEvent)
            analyticsRef.current = true
        }
        // analytics >
    }, [isCartFetching, cards.length])

    useEffect(() => {
        dispatch(setScrollId(''))
        dispatch(setDeliveryMethod('selfPickup'))
        removeShipping()
    }, [])

    return (
        <Step
            title={formatMessage({
                id: 'global.steps.caption_summary',
                defaultMessage: 'Podsumowanie',
            })}
            isButtonDisabled={false}
            isButtonVisible={true}
        >
            <SummaryInfo />

            {sortedCards.map((card, index) => {
                const { cartItemId, name, description, detail } = card
                return (
                    <Section key={cartItemId}>
                        <Box
                            direction="row"
                            isFullWidth
                            justify="space-between"
                            align="center"
                        >
                            <Text variant="heading2">{name}</Text>

                            <Link
                                variant="secondary"
                                icon="pencil"
                                onClick={() => handleBackToCardEditing(index)}
                                data-testid={`unlimited-${index}-button-edit`}
                            >
                                <FormattedMessage
                                    id="annual.summary.edit_details"
                                    defaultMessage="Edytuj dane"
                                />
                            </Link>
                        </Box>
                        <CardSectionDetails
                            cardBox={{
                                ...detail,
                                cartItemId,
                                personDescription: description,
                            }}
                        />
                    </Section>
                )
            })}
        </Step>
    )
}

export default CardSummary
